import { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { isAuthenticated, logout } from '../auth';

export function Brand({ light = false }) {
  return (
    <Link className="flex items-center gap-3 no-underline group shrink-0" to="/" aria-label="Preventive Pest Terminators home">
      <span className="w-11 h-11 md:w-13 md:h-13 grid place-items-center rounded-2xl bg-gradient-to-br from-accent-bright via-accent to-accent-muted shadow-lg group-hover:scale-105 group-hover:rotate-3 transition-transform duration-300 border border-white">
        <svg viewBox="0 0 56 62" className="w-7 md:w-9 drop-shadow-md" role="img" aria-hidden="true">
          <path d="M28 3 50 12v17c0 14-9 25-22 30C15 54 6 43 6 29V12L28 3Z" fill="url(#shield)" />
          <path d="M17 21c8-7 19-7 25 0-4 11-11 17-14 20-4-3-8-10-11-20Z" fill="#a9d957" />
          <path d="M28 17v25M20 29h16" stroke="#174b35" strokeWidth="3" strokeLinecap="round" />
          <defs>
            <linearGradient id="shield" x1="8" y1="3" x2="50" y2="59" gradientUnits="userSpaceOnUse">
              <stop stopColor="#a9d957" />
              <stop offset=".46" stopColor="#76ad43" />
              <stop offset="1" stopColor="#174b35" />
            </linearGradient>
          </defs>
        </svg>
      </span>

      {/* Animated Shimmer Typography with Registered Trademark */}
      <span className="flex flex-col gap-0.5 relative">
        <span className="flex items-start gap-1">
          <span className={`font-heading text-lg md:text-xl font-black tracking-tight leading-none group-hover:scale-105 transition-transform ${light ? 'text-white' : 'text-shimmer-brand'}`}>
            PREVENTIVE
          </span>
          <span className={`text-[10px] md:text-xs font-black leading-none select-none -mt-0.5 ${light ? 'text-accent-bright' : 'text-cta'}`} title="Registered Trademark">
            ®
          </span>
        </span>
        <span className={`text-[8px] md:text-[9px] tracking-[0.28em] font-bold uppercase leading-none pl-0.5 ${light ? 'text-accent-bright/90' : 'text-txt-muted'}`}>
          PEST TERMINATORS
        </span>
      </span>
    </Link>
  );
}

export function Header() {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  const navigate = useNavigate();
  const isAuth = isAuthenticated();

  const handleLogout = () => {
    logout();
    navigate('/');
    close();
  };

  const navLinkClass = ({ isActive }) =>
    `px-4 py-2.5 rounded-xl lg:rounded-full text-sm font-extrabold transition-all no-underline ${isActive
      ? 'bg-gradient-to-r from-accent-soft to-accent/20 text-primary shadow-sm border border-accent/40 font-black'
      : 'text-txt-secondary hover:bg-accent-soft/70 hover:text-primary'
    }`;

  return (
    <header className="h-20 lg:h-24 flex items-center px-3 sm:px-6 lg:px-10 bg-bg/90 backdrop-blur-md sticky top-0 z-50 transition-all">

      {/* Backdrop Dimmer when Mobile Drawer is Open */}
      {open && (
        <div
          className="fixed inset-0 bg-primary-dark/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={close}
        />
      )}

      <nav className="h-16 lg:h-[68px] w-full max-w-7xl mx-auto px-4 lg:px-6 flex items-center justify-between gap-4 rounded-xl lg:rounded-2xl bg-white shadow-xl border border-slate-200/80 relative z-50">

        {/* Brand Logo */}
        <Brand />

        {/* Mobile/iPad Hamburger Toggle */}
        <button
          className="lg:hidden flex items-center justify-center w-10 h-10 rounded-xl border border-border text-primary bg-surface hover:bg-accent-soft transition-all shrink-0"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-label="Toggle navigation"
        >
          {open ? (
            <span className="text-xl font-black text-cta">✕</span>
          ) : (
            <div className="flex flex-col gap-1 w-4">
              <span className="w-full h-0.5 bg-primary rounded-full" />
              <span className="w-full h-0.5 bg-primary rounded-full" />
              <span className="w-full h-0.5 bg-primary rounded-full" />
            </div>
          )}
        </button>

        {/* Desktop Nav Links (lg breakpoint for iPad) */}
        <div className="hidden lg:flex items-center gap-1 p-1 rounded-full bg-accent-soft/50 border border-border/50 shadow-inner">
          <NavLink to="/" onClick={close} className={navLinkClass}>Home</NavLink>
          <NavLink to="/services" onClick={close} className={navLinkClass}>Services</NavLink>
          <NavLink to="/about" onClick={close} className={navLinkClass}>About</NavLink>
          <NavLink to="/contact" onClick={close} className={navLinkClass}>Contact</NavLink>
        </div>

        {/* Desktop Actions (lg breakpoint) */}
        <div className="hidden lg:flex items-center gap-3.5">
          <a className="grid text-primary font-bold text-xs leading-tight no-underline group" href="tel:+918788895445">
            <span className="text-[9px] tracking-widest text-txt-muted font-extrabold flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-cta animate-ping" /> 24/7 HELPLINE
            </span>
            <span className="group-hover:text-cta transition-colors text-sm font-extrabold">87888 95445</span>
          </a>
          {isAuth ? (
            <>
              <Link className="text-primary font-bold text-xs px-4 py-2.5 rounded-full border border-border hover:bg-accent-soft transition-all no-underline shadow-sm hover:shadow-md" to="/admin/users" onClick={close}>
                Admin Panel
              </Link>
              <button onClick={handleLogout} className="text-red-500 font-bold text-xs px-4 py-2.5 rounded-full border border-red-100 hover:bg-red-50 transition-all shadow-sm hover:shadow-md cursor-pointer bg-white">
                Logout
              </button>
            </>
          ) : (
            <Link className="text-primary font-bold text-xs px-4 py-2.5 rounded-full border border-border hover:bg-accent-soft transition-all no-underline shadow-sm hover:shadow-md" to="/login" onClick={close}>
              Admin Login
            </Link>
          )}
          <a className="btn-flash text-white bg-cta hover:bg-cta-hover px-5 py-2.5 rounded-full text-xs font-black shadow-cta flex items-center gap-2 no-underline" href="https://wa.me/918788895445" target="_blank" rel="noreferrer">
            Get Quote <b className="text-accent-bright text-sm">→</b>
          </a>
        </div>

        {/* Clean Light Theme Mobile/iPad Dropdown Drawer */}
        {open && (
          <div className="lg:hidden absolute top-[72px] left-0 right-0 p-4 bg-white/95 backdrop-blur-2xl rounded-2xl border border-slate-200 shadow-2xl z-50 flex flex-col gap-4 animate-rise text-primary">
            <div className="flex flex-col gap-1.5 p-2 rounded-2xl bg-surface-alt/60 border border-slate-200">
              <NavLink to="/" onClick={close} className={({ isActive }) => `px-4 py-2.5 rounded-xl text-sm font-bold transition-all no-underline ${isActive ? 'bg-cta text-white shadow-md' : 'text-primary hover:bg-white/80 hover:text-cta'}`}>Home</NavLink>
              <NavLink to="/services" onClick={close} className={({ isActive }) => `px-4 py-2.5 rounded-xl text-sm font-bold transition-all no-underline ${isActive ? 'bg-cta text-white shadow-md' : 'text-primary hover:bg-white/80 hover:text-cta'}`}>Services</NavLink>
              <NavLink to="/about" onClick={close} className={({ isActive }) => `px-4 py-2.5 rounded-xl text-sm font-bold transition-all no-underline ${isActive ? 'bg-cta text-white shadow-md' : 'text-primary hover:bg-white/80 hover:text-cta'}`}>About</NavLink>
              <NavLink to="/contact" onClick={close} className={({ isActive }) => `px-4 py-2.5 rounded-xl text-sm font-bold transition-all no-underline ${isActive ? 'bg-cta text-white shadow-md' : 'text-primary hover:bg-white/80 hover:text-cta'}`}>Contact</NavLink>
            </div>

            <div className="pt-3 flex flex-col gap-3 border-t border-slate-200">
              <a href="tel:+918788895445" className="w-full py-3.5 px-4 rounded-2xl bg-surface-alt text-primary font-bold text-xs flex items-center justify-between no-underline border border-slate-200 shadow-2xs hover:bg-accent-soft transition-all">
                <span className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-cta animate-ping" /> Call 24/7 Helpline
                </span>
                <span className="font-extrabold text-cta text-sm">87888 95445</span>
              </a>
              {isAuth ? (
                <div className="flex flex-col gap-2.5">
                  <div className="grid grid-cols-2 gap-2.5">
                    <Link to="/admin/users" onClick={close} className="py-3 px-4 rounded-2xl border border-slate-200 text-center font-bold text-xs text-primary no-underline bg-white hover:bg-accent-soft transition-all shadow-2xs">
                      Admin Panel
                    </Link>
                    <button onClick={handleLogout} className="py-3 px-4 rounded-2xl border border-red-200 text-center font-bold text-xs text-red-500 no-underline bg-white hover:bg-red-50 transition-all shadow-2xs cursor-pointer">
                      Logout
                    </button>
                  </div>
                  <a href="https://wa.me/918788895445" target="_blank" rel="noreferrer" className="btn-flash py-3 px-4 rounded-2xl bg-cta text-white text-center font-extrabold text-xs no-underline flex items-center justify-center gap-1 shadow-md">
                    Get Quote →
                  </a>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2.5">
                  <Link to="/login" onClick={close} className="py-3 px-4 rounded-2xl border border-slate-200 text-center font-bold text-xs text-primary no-underline bg-white hover:bg-accent-soft transition-all shadow-2xs">
                    Admin Login
                  </Link>
                  <a href="https://wa.me/918788895445" target="_blank" rel="noreferrer" className="btn-flash py-3 px-4 rounded-2xl bg-cta text-white text-center font-extrabold text-xs no-underline flex items-center justify-center gap-1 shadow-md">
                    Get Quote →
                  </a>
                </div>
              )}
            </div>
          </div>
        )}

      </nav>
    </header>
  );
}

export function Footer() {
  return (
    <footer className="relative mt-14 pt-16 bg-primary-dark text-white rounded-t-[40px] md:rounded-t-[60px] overflow-hidden border-t-4 border-accent">
      {/* 3D Dynamic Curve Top Overlay */}
      <div className="absolute top-0 inset-x-0 h-4 bg-gradient-to-r from-accent via-accent-bright to-cta opacity-80" />
      <div className="absolute top-4 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl pointer-events-none float-slow" />
      <div className="absolute bottom-10 left-10 w-80 h-80 bg-cta/10 rounded-full blur-3xl pointer-events-none float-medium" />

      <div className="max-w-7xl mx-auto px-6 md:px-14 relative z-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 pb-14 border-b border-white/10">

          {/* Brand Info */}
          <div className="space-y-4 lg:col-span-1">
            <div className="bg-white/10 p-3.5 px-4 rounded-2xl backdrop-blur-md border border-white/15 inline-block">
              <Brand light={true} />
            </div>
            <p className="text-sm leading-relaxed text-white/70 mt-3 font-normal">
              Certified, safety-led pest management for residential, commercial & regulated industrial spaces across Maharashtra.
            </p>
          </div>

          {/* Quick Links */}
          <div className="space-y-3">
            <b className="block text-xs text-accent-bright font-bold uppercase tracking-widest mb-4">Quick Navigation</b>
            {[
              ['Home', '/'],
              ['Services Showcase', '/services'],
              ['About PPT', '/about'],
              ['Get In Touch', '/contact'],
              ['Admin Portal', '/login'],
            ].map(([label, href]) => (
              <Link key={label} to={href} className="block text-sm text-white/70 hover:text-accent-bright hover:translate-x-1.5 transition-all no-underline flex items-center gap-2 group">
                <span className="text-accent-bright opacity-0 group-hover:opacity-100 transition-opacity">→</span> {label}
              </Link>
            ))}
          </div>

          {/* Contact Details */}
          <div className="space-y-3">
            <b className="block text-xs text-accent-bright font-bold uppercase tracking-widest mb-4">Direct Contact</b>
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 space-y-2">
              <span className="text-[10px] text-accent-bright font-bold uppercase tracking-wider block">Helpline Numbers</span>
              <a href="tel:+918788895445" className="block text-sm text-white font-bold hover:text-accent-bright transition-colors no-underline">☎ 87888 95445</a>
              <a href="tel:+919850543737" className="block text-sm text-white font-bold hover:text-accent-bright transition-colors no-underline">☎ 98505 43737</a>
            </div>
            <a href="mailto:pptpune2020@gmail.com" className="block text-xs text-white/70 hover:text-white transition-colors no-underline pt-1">
              ✉ pptpune2020@gmail.com
            </a>
          </div>

          {/* Office Address */}
          <div className="space-y-3">
            <b className="block text-xs text-accent-bright font-bold uppercase tracking-widest mb-4">Head Office (Pune)</b>
            <div className="p-4 rounded-2xl bg-white/5 border border-white/10 text-xs text-white/80 leading-relaxed space-y-1">
              <strong className="block text-white text-sm">Preventive Pest Terminators</strong>
              <p className="m-0">Plot No-3/13, Hissa No-1180,</p>
              <p className="m-0">Krushnamai Society, Taljai Pathar,</p>
              <p className="m-0">Dhankawadi, Pune – 411043</p>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-white/50">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-success animate-ping" />
            <span>Operational across 8 Branches in Maharashtra</span>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-1.5 sm:gap-3 text-center">
            <span>© 2026 Preventive Pest Terminators. All rights reserved.</span>
            <span className="hidden sm:inline text-white/20">|</span>
            <a href="https://devforgelab.in" target="_blank" rel="noreferrer" className="text-white/40 hover:text-accent-bright transition-colors no-underline text-[10px] sm:text-xs">
              Designed & Developed by <span className="font-semibold text-white/60 hover:text-accent-bright">DevForgeLab</span>
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
